import type { QuizsetFilterCriteria } from '@/api/gen/_model/quizsetFilterCriteria';
import { QuizsetFilterCriteriaSearchType } from '@/api/gen/_model/quizsetFilterCriteriaSearchType';

export interface BrowseFilterInput {
  searchText?: string | null;
  searchType?: QuizsetFilterCriteria['searchType'] | string | null;
  countryCode?: string;
  checkedOfficialQuiz?: boolean;
  selectedSchoolTypeName?: string;
  selectedGrades?: string[];
  selectedSemesters?: string[];
  selectedSubject?: string;
  isSubSubjectSelected?: boolean;
  selectedPublishers?: string[];
  selectedMapTemplate?: string;
  selectedQuestionSize?: string;
}

export interface SearchFilterInput {
  searchText?: string | null;
  searchType?: QuizsetFilterCriteria['searchType'] | string | null;
  countryCode?: string;
  selectedSchoolTypeName?: string;
  selectedGrades?: string[];
  selectedSemesters?: string[];
  selectedSubject?: string;
  selectedPublishers?: string[];
  checkedOfficialQuiz?: boolean;
  selectedMapTemplate?: string;
  selectedQuestionSize?: string;
}

const normalizeSearchText = (searchText?: string | null) =>
  searchText?.trim().slice(0, 30) ?? '';

const getSearchType = (
  searchType?: QuizsetFilterCriteria['searchType'] | string | null,
) => {
  if (searchType === 'TITLE' || searchType === 'TITLE_CONTENT') {
    return QuizsetFilterCriteriaSearchType.TITLE;
  }

  return (
    (searchType as QuizsetFilterCriteria['searchType']) ||
    QuizsetFilterCriteriaSearchType.ALL
  );
};

const LEGACY_SCHOOL_TYPE_CODE_BY_NAME: Record<string, string> = {
  KINDERGARTEN: '0',
  PRIMARY: '1',
  MIDDLE: '2',
  HIGH: '3',
  COLLEGE: '6',
  OTHER: '9',
};

const toLegacySchoolTypeCode = (selectedSchoolTypeName?: string) => {
  const normalizedSchoolType = selectedSchoolTypeName?.trim().toUpperCase();

  if (!normalizedSchoolType || normalizedSchoolType === 'ALL') {
    return undefined;
  }

  const schoolTypeCode = LEGACY_SCHOOL_TYPE_CODE_BY_NAME[normalizedSchoolType];
  return schoolTypeCode;
};

const toQuizCountRange = (selectedQuestionSize?: string) => {
  if (!selectedQuestionSize) return undefined;

  const [min, max] = selectedQuestionSize.split('-').map(Number);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;

  return { min, max };
};

export type QuizsetSubjectFilter = {
  subject?: string;
  subSubject?: string;
};

export const buildSubjectFilter = ({
  selectedSubject,
  isSubSubjectSelected,
}: Pick<
  BrowseFilterInput,
  'selectedSubject' | 'isSubSubjectSelected'
>): QuizsetSubjectFilter => {
  if (!selectedSubject) return {};

  return isSubSubjectSelected
    ? { subSubject: selectedSubject.slice(2) }
    : { subject: selectedSubject };
};

export const buildSubjectFilterFromSearchSubject = (
  selectedSubject?: string,
): QuizsetSubjectFilter => {
  if (!selectedSubject) return {};

  return selectedSubject.length === 4
    ? { subSubject: selectedSubject.slice(2) }
    : { subject: selectedSubject };
};

const appendSearchText = (
  criteria: QuizsetFilterCriteria,
  searchText?: string | null,
  searchType?: QuizsetFilterCriteria['searchType'] | string | null,
) => {
  const normalizedSearchText = normalizeSearchText(searchText);
  if (!normalizedSearchText) return criteria;

  return {
    ...criteria,
    searchText: normalizedSearchText,
    searchType: getSearchType(searchType),
  };
};

const appendSchoolFilters = (
  criteria: QuizsetFilterCriteria,
  input: Pick<
    SearchFilterInput,
    'selectedSchoolTypeName' | 'selectedGrades' | 'selectedSemesters'
  >,
) => {
  const schoolType = toLegacySchoolTypeCode(input.selectedSchoolTypeName);

  return {
    ...criteria,
    ...(schoolType && { schoolType }),
    ...(schoolType &&
      input.selectedGrades?.length && { schoolGrades: input.selectedGrades }),
    ...(schoolType &&
      input.selectedSemesters?.length && {
        schoolSemesters: input.selectedSemesters,
      }),
  };
};

export const buildBrowseQuizsetFilterCriteria = (
  input: BrowseFilterInput,
): QuizsetFilterCriteria => {
  const withSearchText = appendSearchText(
    {
      countryCode: input.countryCode,
    },
    input.searchText,
    input.searchType,
  );

  const withSchool = appendSchoolFilters(withSearchText, input);
  const quizCountRange = toQuizCountRange(input.selectedQuestionSize);

  return {
    ...withSchool,
    ...(input.checkedOfficialQuiz && { isOfficial: input.checkedOfficialQuiz }),
    ...(input.selectedPublishers?.length && {
      publishers: input.selectedPublishers,
    }),
    ...(input.selectedMapTemplate && {
      templateType: input.selectedMapTemplate,
    }),
    ...(quizCountRange && { quizCountRange }),
  };
};

export const buildInitialSearchQuizsetFilterCriteria = (
  input: SearchFilterInput,
): QuizsetFilterCriteria => {
  const withSearchText = appendSearchText(
    {
      countryCode: input.countryCode,
    },
    input.searchText,
    input.searchType,
  );

  return appendSchoolFilters(withSearchText, input);
};

export const buildActiveSearchQuizsetFilterCriteria = (
  input: SearchFilterInput,
): QuizsetFilterCriteria => {
  const withSearchText = appendSearchText(
    {
      countryCode: input.countryCode,
    },
    input.searchText,
    input.searchType,
  );
  const withSchool = appendSchoolFilters(withSearchText, input);
  const quizCountRange = toQuizCountRange(input.selectedQuestionSize);

  return {
    ...withSchool,
    ...(input.checkedOfficialQuiz && { isOfficial: input.checkedOfficialQuiz }),
    ...(input.selectedPublishers?.length && {
      publishers: input.selectedPublishers,
    }),
    ...(input.selectedMapTemplate && {
      templateType: input.selectedMapTemplate,
    }),
    ...(quizCountRange && { quizCountRange }),
  };
};

export const omitSearchTextCriteria = (
  criteria: QuizsetFilterCriteria,
): QuizsetFilterCriteria => {
  const {
    searchText: _searchText,
    searchType: _searchType,
    ...rest
  } = criteria;
  return rest;
};
